import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { PageHeader, inputCls, btnPrimary, btnTurq } from "../../components/bits";
import { FileText, Download, BadgeCheck, FileSpreadsheet } from "lucide-react";

export default function Reports() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [period, setPeriod] = useState("monthly");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [range, setRange] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (from) p.set("from_date", from);
      if (to) p.set("to_date", to);
      api.get(`/portal/reports/range?${p}`).then((r) => setRange(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [from, to]);

  return (
    <div data-testid="reports-page" className="max-w-2xl">
      <PageHeader title="التقارير" sub="تقارير رسمية أنيقة باسم المدرسة والمنسق ومدير المدرسة — بصيغة PDF" />

      <div className="bg-surface rounded-3xl border border-brd shadow-md p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-turq-100 dark:bg-surface2 flex items-center justify-center">
            <FileText className="w-5 h-5 text-turq-900 dark:text-turq-300" />
          </div>
          <div>
            <div className="font-head font-bold">التقرير الشهري للعمل التطوعي</div>
            <div className="text-[11px] text-mutext">مذيّل ببيانات المنصة وقسم المسؤولية المجتمعية والعمل التطوعي</div>
          </div>
        </div>

        <ul className="text-xs text-mutext space-y-2 pr-1">
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-turq-600" /> مؤشرات المدرسة: الطلاب الفريدون، مؤشر التمكين 90%، الشريحة، نقاط الأثر</li>
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-turq-600" /> جدول الفرص التطوعية خلال الشهر مع القيمة الرئيسية والمشاركين والساعات</li>
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-turq-600" /> قائمة أعلى الطلاب ساعات تطوعية</li>
        </ul>

        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[130px]">
            <label className="text-xs font-bold text-mutext block mb-1.5">نوع التقرير</label>
            <select data-testid="report-period-select" value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls}>
              <option value="monthly">شهري</option>
              <option value="quarterly">فصلي</option>
              <option value="annual">سنوي (بصفحة غلاف رسمية)</option>
            </select>
          </div>
          {period === "monthly" && (
            <div className="flex-1 min-w-[130px]">
              <label className="text-xs font-bold text-mutext block mb-1.5">الشهر</label>
              <input data-testid="report-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} dir="ltr" />
            </div>
          )}
          <button data-testid="report-download-btn"
            onClick={() => window.open(`${API}/portal/reports/monthly?month=${month}&period=${period}`, "_blank")}
            className={btnPrimary}>
            <Download className="w-4 h-4" /> توليد التقرير (PDF)
          </button>
        </div>
      </div>

      <div className="bg-surface rounded-3xl border border-brd shadow-md p-6 space-y-5 mt-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-coral-100 dark:bg-surface2 flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-coral-900 dark:text-coral-300" />
          </div>
          <div>
            <div className="font-head font-bold">تقرير بنطاق تاريخي مخصص</div>
            <div className="text-[11px] text-mutext">يعرض عدد المتطوعين الفعليين (من شارك فعلياً) وليس إجمالي طلاب المدرسة</div>
          </div>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-bold text-mutext block mb-1.5">من تاريخ</label>
            <input data-testid="report-from-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} dir="ltr" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-bold text-mutext block mb-1.5">إلى تاريخ</label>
            <input data-testid="report-to-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} dir="ltr" />
          </div>
          <button data-testid="report-excel-btn"
            onClick={() => window.open(`${API}/portal/reports/export?from_date=${from}&to_date=${to}`, "_blank")}
            className={btnTurq}>
            <FileSpreadsheet className="w-4 h-4" /> تصدير Excel
          </button>
        </div>
        {range && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-range-stats">
            {[[range.opportunities, "إجمالي الفرص"], [range.volunteers, "المتطوعون الفعليون"], [range.hours, "إجمالي الساعات"], [`${range.economic_value} ر.س`, "العائد الاقتصادي"]].map(([v, l]) => (
              <div key={l} className="rounded-2xl bg-beige-300 dark:bg-surface2 p-4 text-center">
                <div className="font-head font-black text-xl text-turq-900 dark:text-turq-300">{v}</div>
                <div className="text-[10.5px] text-mutext mt-1">{l}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
